import React from 'react';
import { Link } from 'react-router-dom';

const Programs = () => (
  <div className="bg-ehh-cream text-ehh-navy">
    {/* Intro Section - Clean and centred */}
    <section className="py-24 px-6 text-center bg-white">
      <div className="container mx-auto max-w-5xl">
        <h1 className="text-4xl md:text-6xl font-bold mb-10 text-ehh-orange">
          Our Thematic Areas/Pillars
        </h1>
        <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed">
          True development begins with dignity. By securing basic needs through Social Protection, strengthening health systems, and investing in Human Capital, we build the essential foundation upon which resilience, self-reliance, and sustainable community transformation are built.
        </p>
      </div>
    </section>

    {/* Pillar 1: Social Protection */}
    <section className="py-24 px-6 bg-ehh-navy/5">
      <div className="container mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div>
          <h2 className="text-3xl md:text-5xl font-bold mb-10">Social Protection</h2>
          <p className="text-lg leading-relaxed mb-8">
            Strategic Focus: Securing immediate human dignity and stability by addressing critical gaps in basic needs and safety.
          </p>
          <p className="text-lg leading-relaxed mb-10">
            Rationale: A person struggling for food or safety cannot focus on education or economic advancement. This pillar establishes the essential foundation upon which all other development is built, directly addressing the most acute vulnerabilities.
          </p>
          <div className="bg-ehh-peach/10 border border-ehh-peach/30 rounded-2xl p-8 shadow-lg">
            <p className="text-xl font-semibold mb-4">Key Initiatives</p>
            <ul className="space-y-3 text-base list-disc list-inside">
              <li>Community Nutrition: Providing consistent, nutritious meals targeting pregnant women, children, and the elderly to combat malnutrition and hunger.</li>
              <li>Psycho-social Support: Offering professional counselling and support services for victims of gender-based violence, teenage pregnancies, and familial distress, creating a safe pathway for healing and stability.</li>
            </ul>
            <p className="mt-6 font-medium">We are transitioning from: Providing meals → to building a foundation for learning and productivity. A child whose nutritional needs are met can concentrate in school. An empowered adult can be involved in production and trade. Our feeding programme is not an end—it is the essential, stabilising base upon which all other development is built.</p>
          </div>
        </div>
        <img 
          src="/assets/images/social-protection-hero.jpg" 
          alt="Community nutrition and support in Zambia" 
          className="rounded-2xl shadow-xl w-full object-cover h-96" 
        />
      </div>
    </section>

    {/* Pillar 2: Integrated Community Health */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <img 
          src="/assets/images/community-health-hero.png" 
          alt="Mobile clinic and health advocacy in Zambian community" 
          className="rounded-2xl shadow-xl w-full object-cover h-96 order-2 md:order-1" 
        />
        <div className="order-1 md:order-2">
          <h2 className="text-3xl md:text-5xl font-bold mb-10 text-center md:text-left">
            Integrated Community Health and Systems Strengthening
          </h2>
          <p className="text-lg leading-relaxed mb-8">
            Focus: Strengthening the local health ecosystem through direct service and systemic advocacy which improves community health outcomes.
          </p>
          <p className="text-lg leading-relaxed mb-10">
            Rationale: Public healthcare facilities in Zambia are overstretched which has created care gaps and the long-term sustainability of local health systems.
          </p>
          <div className="bg-ehh-peach/10 border border-ehh-peach/30 rounded-2xl p-8 shadow-lg">
            <p className="text-xl font-semibold mb-4">Key Initiatives</p>
            <ul className="space-y-3 text-base list-disc list-inside">
              <li>Community Mobile Clinics: Deploying mobile clinics to provide primary healthcare services and referrals in low-income populations.</li>
              <li>Community Health Advocacy: Building the capacity of community members to monitor local health services and advocate for improved quality and accessibility of healthcare services.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* Pillar 3: Human Capital & Economic Resilience */}
    <section className="py-24 px-6 bg-ehh-navy/5">
      <div className="container mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div>
          <h2 className="text-3xl md:text-5xl font-bold mb-10">Human Capital and Economic Resilience</h2>
          <p className="text-lg leading-relaxed mb-8">
            Focus: Equipping youth with lifeskills and resources for sustainable livelihoods.
          </p>
          <p className="text-lg leading-relaxed mb-10">
            Rationale: To break the vicious cycle of poverty by creating opportunities for production.
          </p>
          <div className="bg-ehh-peach/10 border border-ehh-peach/30 rounded-2xl p-8 shadow-lg">
            <p className="text-xl font-semibold mb-4">Key Initiatives</p>
            <ul className="space-y-3 text-base list-disc list-inside">
              <li>Centre for Excellence: A market-driven centre for excellence in skills that transform community member into entrepreneurs.</li>
              <li>Linkages with Public programmes: Assist community members in accessing government empowerment programmes (e.g., social cash transfers, NHIMA, CDF), ensuring public funds reach the intended beneficiaries.</li>
            </ul>
            <p className="mt-6 font-medium">We are transitioning from: Training youth → to architecting a new local economy. We are not just reducing unemployment statistics; we are injecting the market with certified, skilled artisans who will build our community's infrastructure, start businesses, and mentor the next cohort, creating a self-sustaining engine of economic growth.</p>
          </div>
        </div>
        <img 
          src="/assets/images/economic-resilience-hero.jpg" 
          alt="Skills training and entrepreneurship in Zambian community" 
          className="rounded-2xl shadow-xl w-full object-cover h-96" 
        />
      </div>
    </section>

    {/* Synergistic Impact Closing */}
    <section className="py-24 px-6 text-center bg-ehh-orange/10">
      <div className="container mx-auto max-w-5xl">
        <h2 className="text-3xl md:text-5xl font-bold mb-10">Synergistic Impact</h2>
        <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed mb-12">
          A youth stabilised through nutrition graduates from our Centre of Excellence, launches a business, and advocates for improved health services—creating a self-reinforcing cycle of community transformation.
        </p>
        <Link 
          to="/donate" 
          className="inline-block bg-ehh-orange text-ehh-cream px-12 py-5 rounded-lg text-xl font-semibold hover:bg-ehh-coral transition-shadow shadow-xl"
        >
          Support These Programs
        </Link>
      </div>
    </section>
  </div>
);

export default Programs;